<template>
  <div class="relative inline-block" ref="triggerElement" :class="{ 'opacity-50 cursor-not-allowed': disabled }">
    <button 
      type="button"
      @click="handleToggle" 
      data-transpose-button
      class="toolbar-btn flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      :title="$t('editor.transpose') || 'Transpose'"
    >
      <svg class="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"></path>
      </svg>
      <span class="font-semibold text-xs min-w-[16px] text-center">{{ formattedSemitone }}</span>
    </button>
    
    <Teleport to="body">
      <Transition name="fade">
        <div 
          v-if="isOpen" 
          ref="dropdownElement"
          data-transpose-dropdown
          class="fixed w-44 p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-[200] transition-all duration-200"
          :style="dropdownStyle"
          @click.stop
        >
          <div class="flex flex-col items-center gap-2 select-none">
            <div class="flex items-center justify-between w-full">
              <span class="text-xs font-bold text-gray-700 dark:text-gray-200">
                Transpose
              </span>
              <button 
                v-if="currentValue !== 0"
                @click="reset"
                class="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
              >
                Reset
              </button>
            </div>
            <div class="flex items-center justify-between w-full bg-gray-50 dark:bg-slate-900 rounded-md p-1.5 border border-gray-200 dark:border-slate-700">
              <button 
              type="button"
              @click="decrement"
              class="p-1.5 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              title="Transpose down (-1)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M20 12H4"/>
              </svg>
            </button>
            <input 
              type="number"
              v-model.number="inputValue"
              @keydown="onKeyDown"
              @change="onInputChange"
              class="text-sm font-bold text-gray-900 dark:text-white bg-transparent w-12 text-center border-none outline-none focus:outline-none focus:ring-0 rounded px-1"
            />
            <button 
              type="button"
              @click="increment"
              class="p-1.5 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              title="Transpose up (+1)"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
              </svg>
            </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useTeleportDropdown } from '@/composables/useTeleportDropdown.js'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const currentValue = ref(props.modelValue || 0)
const inputValue = ref(props.modelValue || 0)

watch(() => props.modelValue, (newVal) => {
  const num = Number(newVal || 0)
  currentValue.value = num
  inputValue.value = num
})

const formattedSemitone = computed(() => {
  const val = currentValue.value
  if (val > 0) return `+${val}`
  return `${val}`
})

const {
  isOpen,
  triggerElement,
  dropdownElement,
  dropdownStyle,
  toggle,
  close
} = useTeleportDropdown({
  position: 'bottom',
  zIndex: '200'
})

const handleToggle = () => {
  if (props.disabled) return
  toggle()
}

watch(() => props.disabled, (val) => {
  if (val) close()
})

const updateValue = (newVal, delta) => {
  if (delta === 0) return
  currentValue.value = newVal
  inputValue.value = newVal
  emit('update:modelValue', newVal)
  emit('change', { semitone: newVal, delta })
}

const onKeyDown = (event) => {
  if (event.key === 'Enter') {
    event.target.blur()
    return
  }
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter']
  if (allowedKeys.includes(event.key)) return
  if (event.key === '-' || (event.key >= '0' && event.key <= '9')) return
  event.preventDefault()
}

const onInputChange = () => {
  const val = Number(inputValue.value) || 0
  const delta = val - currentValue.value
  updateValue(val, delta)
}

const increment = () => {
  updateValue(currentValue.value + 1, 1)
}

const decrement = () => {
  updateValue(currentValue.value - 1, -1)
}

const reset = () => {
  const delta = -currentValue.value
  updateValue(0, delta)
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Hide number input spinners */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  border: none;
  outline: none;
}
</style>
